import React, { useState } from 'react';
import { Btn, Msg } from '../../kit';
import { inputCls } from '../emailKit';
import { num } from './shared';
import { VaryingNotice, OfferBuckets } from '../VaryingNotice';

/**
 * The placeholders a campaign uses, and what each becomes for whoever has no
 * value (owner, 2026-10-01: "when there is no first name it says 'there' —
 * make it changeable per campaign, warn about the people with missing values
 * so we can review and fix them, the site url etc. too, in a small dropdown").
 *
 * One row per placeholder in the e-mail; its ▾ opens a small panel: the
 * fallback for this campaign (blank = left empty), or for a site-wide value
 * (site_url, upgrade_url, the counts) the value this campaign uses. With an
 * audience, each row says how many of the selected recipients have no value
 * and lists them, so they can be left out — or their data fixed through
 * People → Import (fill blanks) — before anything is sent.
 *
 * Backed by POST email/target_placeholders (admin_email_targeting
 * .placeholder_report); the same report comes back with every preview.
 */

export type MissingPerson = { user_id: string; email: string; name: string };
export type PlaceholderRow = {
  /** person: per recipient, may be missing · site: same for everyone, can be
   *  overridden · fixed: always filled · empty: never filled in a campaign. */
  name: string; meaning: string; kind: 'person' | 'site' | 'fixed' | 'empty';
  // person
  missing?: number; fallback?: string; default_fallback?: string; fallback_set?: boolean;
  /** Takes this placeholder's campaign fallback when it has none of its own
   *  ({name} follows {first_name}). */
  follows?: string | null;
  people?: MissingPerson[];
  // site
  value?: string; site_value?: string; overridden?: boolean;
  /** Differs per person (the per-industry new offer count): who gets which value. */
  varies?: OfferBuckets;
};
export type PlaceholderReport = {
  placeholders: PlaceholderRow[]; checked: number; total: number; people_with_missing: number;
  audience?: boolean; unknown?: string[];
};

const URL_KEYS = new Set(['site_url', 'upgrade_url']);
const urlProblem = (v: string): string => {
  if (!v) return '';
  if (!/^https?:\/\/[^\s{}<>"']+$/i.test(v)) return 'Must be a full link, starting with https://';
  try {
    const host = new URL(v).hostname.toLowerCase();
    if (host !== 'universitytech.io' && !host.endsWith('.universitytech.io')) {
      return 'Not a universitytech.io link: it is sent as is, without per-person campaign tracking.';
    }
  } catch { return 'Must be a full link, starting with https://'; }
  return '';
};
const quoted = (v: string) => (v ? `“${v}”` : 'left blank');

const chip = 'px-1.5 py-0.5 rounded border border-primary-300 bg-primary-100/50 text-[11px] text-grayscale-1300 font-mono';

export const PlaceholderFallbacks: React.FC<{
  report: PlaceholderReport | null; loading: boolean; error: string;
  /** The report is for an earlier version of the content or audience. */
  updating: boolean;
  fallbacks: Record<string, string>; overrides: Record<string, string>;
  /** value null = back to the default. */
  onFallback: (name: string, value: string | null) => void;
  onOverride: (name: string, value: string | null) => void;
  /** Leave these people out of the campaign (untick, or drop from the picked list). */
  onLeaveOut?: (ids: string[]) => void;
  /** A CSV of who is missing `name`; `missing` is the whole count (the
   *  report lists the first 500, so the parent fetches the rest). */
  onCsv?: (name: string, people: MissingPerson[], missing: number) => void;
}> = ({ report, loading, error, updating, fallbacks, overrides, onFallback, onOverride, onLeaveOut, onCsv }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [who, setWho] = useState<Record<string, boolean>>({});
  const rows = report?.placeholders || [];
  const settable = rows.filter(r => r.kind === 'person' || r.kind === 'site');
  const fixed = rows.filter(r => r.kind === 'fixed');
  const empty = rows.filter(r => r.kind === 'empty');
  const audience = !!report?.audience;
  const total = report?.total || 0;
  const checked = report?.checked || 0;
  const warn = rows.filter(r => r.kind === 'person' && (r.missing || 0) > 0);
  // Over the cap nobody can be unticked (the server refuses), so the people
  // are listed but not offered to leave out.
  const leaveOut = total > checked ? undefined : onLeaveOut;
  const toggle = (n: string) => setOpen(o => ({ ...o, [n]: !o[n] }));

  if (!report && !loading && !error) return null;
  return (
    <div id="campaign-placeholders" className="rounded-lg border border-grayscale-300 bg-white scroll-mt-4">
      <div className="px-3 py-2 flex flex-wrap items-center gap-2 border-b border-grayscale-200">
        <span className="text-sm font-bold text-grayscale-1300">Placeholders in this e-mail</span>
        <span className="text-xs text-grayscale-1100">
          {rows.length ? `${rows.length} used` : loading ? 'checking…' : 'none used'}
          {audience && total > 0 && <> · checked against {num(checked)}{checked < total ? ` of ${num(total)}` : ''} recipient{total === 1 ? '' : 's'}</>}
          {!audience && rows.length > 0 && ' · pick the audience to see who is missing a value'}
        </span>
        {(loading || updating) && <span className="text-[11px] text-grayscale-1100">updating…</span>}
      </div>
      {error && <div className="px-3 pt-2"><Msg kind="error">{error}</Msg></div>}
      {warn.length > 0 && (
        <div className="mx-3 mt-2 text-xs rounded-lg px-3 py-2 border border-amber-200 bg-amber-50 text-amber-800">
          {num(report?.people_with_missing || 0)} of the {num(checked)} recipients are missing at least one value:{' '}
          {warn.map((r, i) => (
            <span key={r.name}>
              {i > 0 && ' · '}
              <button type="button" className="underline underline-offset-2" onClick={() => setOpen(o => ({ ...o, [r.name]: true }))}>
                {`{${r.name}}`}
              </button>{' '}
              {num(r.missing || 0)} → <span className={!r.fallback ? 'text-red-700 font-bold' : ''}>{quoted(r.fallback || '')}</span>
            </span>
          ))}
          . Open a placeholder to change what they get, or see who they are.
        </div>
      )}
      {rows.filter(r => r.varies).map(r => (
        <VaryingNotice key={`vary-${r.name}`} name={r.name} data={r.varies} className="mx-3 mt-2" />
      ))}
      <div className="flex flex-col divide-y divide-grayscale-200 px-3 py-1">
        {settable.map(r => {
          const isOpen = !!open[r.name];
          const miss = r.missing || 0;
          const person = r.kind === 'person';
          const has = (k: string) => Object.prototype.hasOwnProperty.call(fallbacks, k);
          const own = has(r.name);
          // Without its own value: what it inherits ({name} takes the
          // campaign's {first_name} fallback), else the default.
          const following = !!r.follows && has(r.follows);
          const inherited = following ? fallbacks[r.follows as string] : (r.default_fallback ?? '');
          const fbValue = own ? fallbacks[r.name] : inherited;
          const ovValue = overrides[r.name] ?? '';
          const urlNote = URL_KEYS.has(r.name) ? urlProblem(ovValue.trim()) : '';
          return (
            <div key={r.name} className="py-1.5">
              <button type="button" onClick={() => toggle(r.name)} aria-expanded={isOpen}
                className="w-full flex flex-wrap items-center gap-2 text-left">
                <span className={chip}>{`{${r.name}}`}</span>
                {person ? (
                  audience ? (
                    miss > 0 ? (
                      <span className="text-xs text-amber-800">
                        ⚠ {num(miss)} of {num(checked)} have none →{' '}
                        <span className={fbValue ? 'text-grayscale-1300' : 'text-red-700 font-bold'}>{quoted(fbValue)}</span>
                      </span>
                    ) : <span className="text-xs text-green-700">✓ everyone has one</span>
                  ) : (
                    <span className="text-xs text-grayscale-1100">if missing → {quoted(fbValue)}</span>
                  )
                ) : (
                  <span className="text-xs text-grayscale-1100 truncate max-w-[28rem]" title={r.value}>
                    {r.overridden || ovValue ? <b className="text-grayscale-1300">this campaign: </b> : 'site value: '}
                    {ovValue || r.value || r.site_value || '—'}
                  </span>
                )}
                {person && own && <span className="text-[10px] rounded border border-primary-300 px-1 text-primary-700">changed</span>}
                <span className="ml-auto text-xs text-grayscale-1100">{isOpen ? '▴' : '▾'}</span>
              </button>
              {isOpen && (
                <div className="mt-1.5 ml-1 pl-3 border-l-2 border-primary-200 flex flex-col gap-1.5">
                  {r.meaning && <div className="text-[11px] text-grayscale-1100">{r.meaning}</div>}
                  {person ? (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="text-xs text-grayscale-1200" htmlFor={`fb-${r.name}`}>When someone has none, write</label>
                        <input id={`fb-${r.name}`} value={fbValue} maxLength={200}
                          onChange={e => onFallback(r.name, e.target.value)}
                          placeholder="(left blank)" className={`${inputCls} !h-8 !w-56`} />
                        {own && (
                          <button type="button" className="text-xs underline underline-offset-2 text-primary-og-600"
                            onClick={() => onFallback(r.name, null)}>
                            back to {following ? `{${r.follows}}'s ` : ''}{quoted(inherited)}
                          </button>
                        )}
                      </div>
                      {r.follows && !own && (
                        <div className="text-[11px] text-grayscale-1100">
                          {following ? `Follows the {${r.follows}} fallback, unless set here.` : `Follows the {${r.follows}} fallback when that one is changed, unless set here.`}
                        </div>
                      )}
                      {!fbValue && miss > 0 && (
                        <div className="text-[11px] text-red-700">
                          Left blank for {num(miss)} people — check the sentence still reads well without it.
                        </div>
                      )}
                      {audience && miss > 0 && (
                        <div className="flex flex-col gap-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <button type="button" className="text-xs underline underline-offset-2 text-primary-og-600"
                              onClick={() => setWho(w => ({ ...w, [r.name]: !w[r.name] }))}>
                              {who[r.name] ? 'Hide who' : `Show the ${num(miss)} without one`}
                            </button>
                            {leaveOut && (r.people || []).length > 0 && (
                              <Btn small onClick={() => {
                                const ids = (r.people || []).map(p => p.user_id).filter(Boolean);
                                if (window.confirm(`Leave ${num(ids.length)} people without {${r.name}} out of this campaign?`)) leaveOut(ids);
                              }}>
                                Leave {(r.people || []).length < miss ? `the ${num((r.people || []).length)} listed` : `all ${num(miss)}`} out
                              </Btn>
                            )}
                            {!leaveOut && onLeaveOut && (
                              <span className="text-[11px] text-grayscale-1100">
                                Over the {num(checked)} cap — narrow the audience before leaving people out.
                              </span>
                            )}
                            {onCsv && (r.people || []).length > 0 && (
                              <Btn small onClick={() => onCsv(r.name, r.people || [], miss)}
                                title="All of them. To fill the gaps: People → Import with “fill blanks”, then preview again">
                                CSV of all {num(miss)}
                              </Btn>
                            )}
                          </div>
                          {who[r.name] && (
                            <div className="max-h-48 overflow-y-auto rounded border border-grayscale-200">
                              {(r.people || []).map(p => (
                                <div key={p.user_id || p.email} className="flex items-center gap-2 px-2 py-1 text-xs odd:bg-grayscale-200/40">
                                  <span className="text-grayscale-1300 truncate">{p.email}</span>
                                  {p.name && <span className="text-grayscale-1100 truncate">{p.name}</span>}
                                  {leaveOut && p.user_id && (
                                    <button type="button" className="ml-auto text-[11px] underline underline-offset-2 text-grayscale-1100"
                                      onClick={() => leaveOut([p.user_id])}>leave out</button>
                                  )}
                                </div>
                              ))}
                              {(r.people || []).length < miss && (
                                <div className="px-2 py-1 text-[11px] text-grayscale-1100">
                                  … and {num(miss - (r.people || []).length)} more (the first {num((r.people || []).length)} are listed).
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="text-xs text-grayscale-1200" htmlFor={`ov-${r.name}`}>Value for this campaign</label>
                        <input id={`ov-${r.name}`} value={ovValue} maxLength={200}
                          onChange={e => onOverride(r.name, e.target.value === '' ? null : e.target.value)}
                          placeholder={r.site_value || ''} className={`${inputCls} !h-8 !w-80`} />
                        {ovValue && (
                          <button type="button" className="text-xs underline underline-offset-2 text-primary-og-600"
                            onClick={() => onOverride(r.name, null)}>back to the site's value</button>
                        )}
                      </div>
                      <div className="text-[11px] text-grayscale-1100">
                        Same for everyone. Empty = the site's own value ({r.site_value || '—'}).
                      </div>
                      {urlNote && <div className={`text-[11px] ${urlNote.startsWith('Must') ? 'text-red-700' : 'text-amber-800'}`}>{urlNote}</div>}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {fixed.length > 0 && (
        <div className="px-3 pb-2 text-[11px] text-grayscale-1100">
          Always filled for everyone: {fixed.map(r => `{${r.name}}`).join(', ')}.
        </div>
      )}
      {empty.length > 0 && (
        <div className="px-3 pb-2 text-[11px] text-amber-800">
          Left empty in a campaign: {empty.map(r => `{${r.name}}`).join(', ')} — the win-back code is per person and
          given once, when a trial lapses, so a group send never includes it.
        </div>
      )}
    </div>
  );
};

export default PlaceholderFallbacks;
